'use client'

import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { CheckIcon } from 'primitives/icons'
import { tw } from 'utils/classnames'
import { useTranslations, useLocale } from 'next-intl'
import { OneClickExtensionButton } from 'payments/one-click-yearly-extension-button'
import type { Language } from 'codegen/types'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { BannerContainer } from './container'

interface Props {
  banner: Omit<
    NonNullable<SiteDataQuery['siteData']['allAccessYearlyExtendBanner']>,
    '__typename' | 'type'
  > & {
    type: string
  }
}

const defaultColorsConfig = {
  debug: false,
  label: 'Purchase Now',
  bgColor: '#3ab561',
  hoverBgColor: '#248142',
  confirmBgColor: '#3ab561',
  confirmHoverBgColor: '#248142',
  textColor: '#fff',
  lang: 'en',
}

export function AllAccessYearlyExtend({ banner }: Props) {
  const t = useTranslations('cf_banners')

  const locale = useLocale() as Language
  const { features, headline, buttonText } = banner

  function onButtonClick() {
    const isDailyGiftsPage = window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)
    savePaymentModalEntryPoint(
      isDailyGiftsPage
        ? PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
        : PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV,
    )

    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  const backgroundImage: string | null = banner.backgroundImage
  const checkIconColor = '#3ab561'

  return (
    <div
      style={{
        '--checkIconColor': checkIconColor,
      }}
    >
      <BannerContainer backgroundImage={backgroundImage} bannerType={banner.type}>
        <div className="relative -mt-[2px] w-full">
          <div
            className="text-12 md:text-16 [&_.u-bold]:text-16 mb-[10px] text-center align-top leading-[28px] tracking-[0.3px] text-[#2A1F3F] md:text-start md:leading-[28px] [&_.u-bold]:font-bold md:[&_.u-bold]:text-[24px] [&_.u-uppercase]:uppercase"
            dangerouslySetInnerHTML={{ __html: headline }}
          />
          <div className="mb-4 flex flex-wrap items-center justify-center md:hidden">
            <span
              className="flex min-w-[260px] md:w-full"
              id="oneClickYearlyExtensionButtonMountContainer"
              onClick={onButtonClick}
            >
              <OneClickExtensionButton
                lang={locale}
                label={buttonText}
                colors={defaultColorsConfig}
                className="w-fit px-10 uppercase tracking-[0.9px]"
              />
            </span>
          </div>

          {features ? (
            <ul className="mt-[10px] hidden md:block">
              {features.map((feat, index) => {
                return (
                  <li
                    key={index}
                    className={tw(
                      'my-1 flex items-center leading-[1.618]',
                      index > 0 && 'hidden md:flex',
                    )}
                  >
                    <CheckIcon className="mr-1.5 text-[--checkIconColor]" />
                    <span dangerouslySetInnerHTML={{ __html: feat }} />
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        <div className="mr-[8.333%] hidden h-full md:block">
          <div className="-mt-[20px] flex flex-col items-center whitespace-nowrap tracking-[0.9px]">
            <span
              className="flex min-w-[260px] justify-center md:w-full"
              id="oneClickYearlyExtensionButtonMountContainerMd"
              onClick={onButtonClick}
            >
              <OneClickExtensionButton
                lang={locale}
                label={buttonText}
                colors={defaultColorsConfig}
                className="w-fit px-10 uppercase"
              />
            </span>

            <p className="mt-[9px] text-xs">{t('cancel_anytime')}</p>
          </div>
        </div>
      </BannerContainer>
    </div>
  )
}
